<template>
  <div class="tag-menu">
    <div class="tag-menu__inner">
      <h5 class="tag-menu__title">Edit Marker</h5>
      <div class="tag-menu__col">
        <div class="tag-menu__block">
          <label>Font Size</label>
          <input
            class="form-control"
            type="number"
            v-model.number="value.fontSize"
          />
        </div>

        <div v-if="value && value.padding" class="tag-menu__block">
          <label style="flex-shrink: 0; margin-right: 10px">Padding</label>
          <div class="group-block__inputs">
            <div class="group-block__input-wrapper">
              <label>Top</label>
              <input
                class="form-control"
                type="number"
                step="0.01"
                v-model="value.padding[0]"
              />
            </div>
            <div class="group-block__input-wrapper">
              <label>Bottom</label>
              <input
                class="form-control"
                type="number"
                step="0.01"
                v-model="value.padding[2]"
              />
            </div>
            <div class="group-block__input-wrapper">
              <label>Left</label>
              <input
                class="form-control"
                type="number"
                step="0.01"
                v-model="value.padding[3]"
              />
            </div>
            <div class="group-block__input-wrapper">
              <label>Right</label>
              <input
                class="form-control"
                type="number"
                step="0.01"
                v-model="value.padding[1]"
              />
            </div>
          </div>
        </div>

        <!-- Background colour (moved above text colour) -->
        <div class="tag-menu__block">
          <div class="inline-label">
            <label>Background Colour</label>
            <div class="colour-controls">
              <div
                class="btn-colour"
                :style="{ backgroundColor: value.backgroundColour }"
                @click="toggleColourWidget('backgroundColour')"
              ></div>

              <!-- eyedropper -->
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm colour-eye-button"
                @click="pickColour('backgroundColour')"
              >
                <fa :icon="['fas', 'eye-dropper']" />
              </button>
            </div>
          </div>

          <verte
            v-if="colourWidget === 'backgroundColour'"
            class="colour-picker"
            display="widget"
            picker="square"
            model="rgb"
            v-model="value.backgroundColour"
            :showHistory="true"
            :colorHistory.sync="ColourHistory"
          />
        </div>

        <!-- Text colour -->
        <div class="tag-menu__block">
          <div class="inline-label">
            <label>Text Colour</label>
            <div class="colour-controls">
              <div
                class="btn-colour"
                :style="{ backgroundColor: value.textColour }"
                @click="toggleColourWidget('textColour')"
              ></div>

              <!-- eyedropper -->
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm colour-eye-button"
                @click="pickColour('textColour')"
              >
                <fa :icon="['fas', 'eye-dropper']" />
              </button>
            </div>
          </div>

          <verte
            v-if="colourWidget === 'textColour'"
            class="colour-picker"
            display="widget"
            picker="square"
            model="rgb"
            v-model="value.textColour"
            :showHistory="true"
            :colorHistory.sync="ColourHistory"
          />
        </div>

        <!-- Tail position / height -->
        <div class="tag-menu__block">
          <label>Tail Position</label>
          <select v-model="value.tailPosition" class="form-control">
            <option value="none">No Tail</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="bottom-center">Bottom Center</option>
            <option value="bottom-right">Bottom Right</option>
            <option value="top-left">Top Left</option>
            <option value="top-center">Top Center</option>
            <option value="top-right">Top Right</option>
            <option value="left-center">Left Center</option>
            <option value="right-center">Right Center</option>
          </select>
        </div>

        <div class="tag-menu__block">
          <label>Tail Height</label>
          <input
            class="form-control"
            type="number"
            v-model.number="value.tailHeight"
          />
        </div>

        <!-- Rotation -->
        <div class="tag-menu__block">
          <label>Rotation</label>
          <input
            class="form-control"
            type="number"
            v-model.number="value.rotation"
          />
        </div>

        <!-- Box shadow -->
        <div class="tag-menu__block tag-menu__block--inline">
          <label style="flex-shrink: 0; margin-right: 10px">Box Shadow</label>
          <toggle-button
            v-model="value.boxShadow"
            color="#28a745"
            :sync="true"
            :labels="false"
            :width="40"
            :height="20"
            style="font-weight: 400"
          />
        </div>

        <div class="tag-menu__block tag-menu__block--inline">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="$emit('duplicate-marker')"
          >
            Duplicate Marker
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="$emit('remove-marker')"
          >
            Remove Marker
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Verte from "verte";
import "verte/dist/verte.css";

export default {
  props: {
    value: Object,
    colourHistory: { type: Array, default: () => [] },
  },

  components: {
    Verte,
  },

  data() {
    return {
      colourWidget: "",
      ColourHistory: [],
    };
  },

  watch: {
    colourHistory: {
      immediate: true,
      handler(v) {
        this.ColourHistory = Array.isArray(v) ? [...v] : [];
      },
    },
  },

  methods: {
    toggleColourWidget(key) {
      this.colourWidget = this.colourWidget === key ? "" : key;
    },

    // native eyedropper for text/background colours (Chromium only)
    async pickColour(key) {
      // does browser eyedropper exist check
      if (typeof window === "undefined" || !("EyeDropper" in window)) {
        this.$notify &&
          this.$notify({
            message: "Eyedropper not supported in this browser.",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "warning",
          });
        return;
      }

      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open(); // opens native eyedropper

        if (this.value && this.value) {
          // key is 'backgroundColour' or 'textColour'
          this.$set(this.value, key, result.sRGBHex);
        }

        // optionally close Verte after picking
        this.colourWidget = "";
      } catch (err) {
        // user cancelling colour picker, catch & do nothing
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("Eyedropper error", err);
        this.$notify &&
          this.$notify({
            message: "Error using eyedropper.",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger",
          });
      }
    },

    reset() {
      if (window.confirm("Are you sure you want to reset the marker styles?")) {
        // left intentionally empty for now
      }
    },
  },
};
</script>
